import { useRouter } from 'next/router';
import { memo, useCallback } from 'react';

import { useTrackTeamInviteCtaClicked } from '@air/analytics';
import { WorkspaceMemberType } from '@air/api/types';
import { SettingsIcon, UserIcon, UserRoundPlusIcon, UsersIcon } from '@air/design-icons';
import { Badge } from '@air/primitive-badge';
import { DropdownMenuItem } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';

import { InviteMembersModal } from '~/components/Modals/InviteMembers/InviteMembersModal';
import { WorkspaceHeaderInformationDropdownTeamInviteNudge } from '~/components/v2/components/workspace/workspace-header/WorkspaceHeaderInformationDropdownTeamInviteNudge';
import { Routes } from '~/constants/routes';
import { useTeamInviteNudgeEligibility } from '~/hooks/useTeamInviteNudgeEligibility';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspaceMember } from '~/swr-hooks/members/useCurrentWorkspaceMember';
import { useWorkspaceMembers } from '~/swr-hooks/members/useWorkspaceMembers';
import { useShouldShowStorageUsage } from '~/swr-hooks/subscriptions/useShouldShowStorageUsage';
import { useStorage } from '~/swr-hooks/subscriptions/useStorage';
import { useCreditStats } from '~/swr-hooks/workspaces/useCreditStats';
import { canAddWorkspaceMembers, canSeeWorkspaceMembers } from '~/utils/permissions/workspacePermissions';

export const WorkspaceHeaderInformationDropdownWorkspaceItems = memo(() => {
  const { ui2026 } = useTemporaryFeatureFlags();
  const router = useRouter();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const { members } = useWorkspaceMembers();
  const { trackTeamInviteCtaClicked } = useTrackTeamInviteCtaClicked();
  const { isTeamInviteNudgeEligible } = useTeamInviteNudgeEligibility();
  const [showInviteMembersModal] = useAirModal(InviteMembersModal);
  const { shouldShowStorageUsage } = useShouldShowStorageUsage();
  const { data: storage } = useStorage();
  const { data: creditStats } = useCreditStats();
  const { currentWorkspaceMember } = useCurrentWorkspaceMember();

  const canViewMembers = canSeeWorkspaceMembers(permissions);
  const canEditMembers = canAddWorkspaceMembers(permissions);
  const hasCreditUsageDropdownSection =
    !ui2026 &&
    !!creditStats?.allocated &&
    !!currentWorkspaceMember &&
    currentWorkspaceMember.type !== WorkspaceMemberType.guest;
  const hasTopDropdownSections = (shouldShowStorageUsage && !!storage) || hasCreditUsageDropdownSection;

  const handleInviteMembers = useCallback(() => {
    if (isTeamInviteNudgeEligible) {
      trackTeamInviteCtaClicked({ location: 'workspace_settings_dropdown' });
    }

    showInviteMembersModal({ location: 'workspace-dropdown' });
  }, [isTeamInviteNudgeEligible, showInviteMembersModal, trackTeamInviteCtaClicked]);

  const handleWorkspaceSettings = useCallback(() => {
    router.push(Routes.workspaceSettings.members);
  }, [router]);

  return (
    <>
      {isTeamInviteNudgeEligible ? (
        <WorkspaceHeaderInformationDropdownTeamInviteNudge
          onInviteMembers={handleInviteMembers}
          shouldRoundTopCorners={!hasTopDropdownSections}
        />
      ) : (
        canEditMembers && (
          <DropdownMenuItem
            data-id="INVITE_USERS"
            label="Invite users"
            onSelect={handleInviteMembers}
            prefix={
              ui2026 ? (
                <UsersIcon aria-hidden className="ads:text-icon-subtle text-gray-9" size="sm" />
              ) : (
                <UserRoundPlusIcon size="sm" />
              )
            }
            suffix={
              !!members?.allMembers.length && (
                <Badge
                  appearance="pill"
                  className="ads:group-hover/dropdown-menu-item:bg-ads-gray-5 group-hover/dropdown-menu-item:bg-gray-5"
                  size="medium"
                >
                  <UserIcon size="xs" />
                  {members.allMembers.length}
                </Badge>
              )
            }
          />
        )
      )}
      {canViewMembers && (
        <DropdownMenuItem
          data-id="WORKSPACE_SETTINGS_LINK"
          label="Workspace settings"
          onSelect={handleWorkspaceSettings}
          prefix={
            ui2026 ? (
              <SettingsIcon aria-hidden className="ads:text-icon-subtle text-gray-9" size="sm" />
            ) : (
              <SettingsIcon size="sm" />
            )
          }
        />
      )}
    </>
  );
});

WorkspaceHeaderInformationDropdownWorkspaceItems.displayName = 'WorkspaceHeaderInformationDropdownWorkspaceItems';
